import type { FileRecord } from "@cohesive/api-client";
import type { Entity } from "@cohesive/api-entities";
import { memo } from "react";
import type { BlockActionHandler } from "../../../lib/blockActions";
import type { BlockDisplay } from "../../../lib/blockDisplay";
import type { BlockStateSource } from "../../../lib/blockStateSource";
import type {
  BlockEntryPoint,
  BlockToParentMessage,
} from "../../../lib/htmlBlockRuntime";
import { isPageFileRecord } from "../../FileRecordPreview/FileRecordPreview";
import type { ContextMenuAction, Process } from "../BaseCard";
import {
  CanvasLinkCard,
  type CanvasLinkCardHydration,
  isCanvasLinkEntity,
} from "../CanvasLinkCard";
import { EntityCard } from "../EntityCard/EntityCard";
import { DocumentCardWell } from "./DocumentCardWell";
import { HtmlBlockCardSwitch } from "./HtmlBlockCardSwitch";
import type { IframeMountPolicy } from "./useIframeMountAdmission";

export type CanvasEntityCardProps = {
  entity: Entity;
  /** Reference appearance — forces the footer on image/video tiles so the
   *  provenance badge always has a legible home. */
  isReference?: boolean;
  actions?: ContextMenuAction[];
  processes?: Process[];
  /** Blocks report the gesture's iframe-content coordinates so an edit entry
   *  can land the caret under the pointer; other cards ignore the argument. */
  onDoubleClick?: (point?: BlockEntryPoint) => void;
  onQuickChatSubmit?: (message: string) => void;
  onDeleteClick?: () => void;
  onDetailClick?: () => void;
  toolbarMode?: "hover" | "external";
  chatOpen?: boolean;
  onChatOpenChange?: (open: boolean) => void;
  className?: string;
  style?: React.CSSProperties;
  selected?: boolean;
  /** The user is inside the HTML block (interact/edit) — blue focus chrome. */
  focused?: boolean;
  /** True while the node's NodeResizer is active. Forces iframe pointer-events
   *  off while resizing to prevent the cursor being captured by the iframe. */
  resizing?: boolean;
  /** True when the HTML file record iframe should receive pointer events (interactive mode). */
  isInteractive?: boolean;
  /** Canvas video upload handoff — revoke local blob after server video paints. */
  onVideoDisplayReady?: () => void;
  /** The thumbnail failed on its newest link: the owner can re-read the row. */
  onMediaError?: () => void;

  // ── HTML block pass-throughs (see the block-vs-thumb branch below) ────────
  /** Block display model; presence routes the record to the live block
   *  card (documents render the preview tile). */
  display?: BlockDisplay;
  /** Active app theme, threaded down so the injected iframe color-scheme can
   *  match the embedder's (a mismatch breaks transparent blocks). */
  theme?: "light" | "dark";
  /** Async rewrite applied to fetched HTML before rendering (presign swap). */
  transformHtml?: (html: string) => Promise<string> | string;
  /** HTML cards only: `"progressive"` paces iframe mounts through the shared
   *  admission scheduler (public share view); default `"eager"` mounts
   *  immediately — the editor's unchanged behavior. */
  mountPolicy?: IframeMountPolicy;
  /** HTML cards only: server-rendered card preview shown until the live
   *  iframe is up (progressive-mount placeholder). */
  previewImageUrl?: string | null;
  /** Edit session active: the block runtime makes islands contenteditable. */
  islandsEditable?: boolean;
  /** Stash refetched content instead of committing it, so the iframe never
   *  remounts mid edit-session. */
  contentFrozen?: boolean;
  /** Capability-aware hover pill text ("Double-click to interact" / "…to
   *  edit text" / "…to open"); null hides the pill. */
  hintLabel?: string | null;
  /** Hovering the card faintly outlines editable islands (host-driven). */
  islandHint?: boolean;
  /** Caret target for a double-click edit entry, in content coordinates. */
  editFocusPoint?: BlockEntryPoint | null;
  /** Runtime announced itself; carries the live editable-island count. */
  onRuntimeReady?: (info: { islands: number }) => void;
  /** Runtime messages from the block (island edits, flush/exit requests). */
  onBlockMessage?: (msg: BlockToParentMessage) => void;
  /** A same-origin link in the block, as a path for the host's router;
   *  `false` declines it (see HtmlBlockCardCanvas.onNavigate). */
  onBlockNavigate?: (to: string) => boolean | void;
  /** Runs `cohesive.navigate / chat / generate` from an HTML block as the
   *  viewer (see HtmlBlockCardCanvas.onAction). */
  onBlockAction?: BlockActionHandler;
  /** Forward single clicks into a resting block (the click-through doorway). */
  blockClickThrough?: boolean;
  /** A forwarded click hit a control — enter inline interactive mode. */
  onBlockInteractiveClickThrough?: () => void;
  /** Backs `cohesive.state` in an HTML block. */
  blockState?: BlockStateSource;

  /** Canvas link cards only: live hero / location resolved by the host from
   *  the linked canvas record (the entity carries just id + name). */
  canvasLink?: CanvasLinkCardHydration;
  /** Canvas link cards only: the corner open button's handler. */
  onCanvasLinkOpen?: () => void;
};

/**
 * Router for canvas-specific variants. Canvas-only shape: HTML file records
 * (iframe-hosted). Every other entity falls through to the shared `EntityCard`.
 *
 * Memoised: the canvas node above re-renders on zoom steps, hover and
 * selection, and none of those should rebuild the card subtree
 * (`EntityCard` → `FileRecordCard`, or the live block/document frame).
 * Callers keep handler and style props referentially stable.
 */
export const CanvasEntityCard = memo(function CanvasEntityCard({
  entity,
  actions,
  processes,
  onDoubleClick,
  onQuickChatSubmit,
  onDeleteClick,
  onDetailClick,
  toolbarMode,
  chatOpen,
  onChatOpenChange,
  className,
  style,
  selected,
  focused,
  resizing,
  isInteractive,
  onVideoDisplayReady,
  onMediaError,
  display,
  theme,
  transformHtml,
  mountPolicy,
  previewImageUrl,
  islandsEditable,
  contentFrozen,
  hintLabel,
  islandHint,
  editFocusPoint,
  onRuntimeReady,
  onBlockMessage,
  onBlockNavigate,
  onBlockAction,
  blockClickThrough,
  onBlockInteractiveClickThrough,
  blockState,
  isReference,
  canvasLink,
  onCanvasLinkOpen,
}: CanvasEntityCardProps) {
  // ── Canvas link — hero preview tile filling the node box ─
  if (isCanvasLinkEntity(entity)) {
    return (
      <CanvasLinkCard
        {...entity.data}
        {...canvasLink}
        embedded
        onOpenClick={onCanvasLinkOpen}
        selected={selected}
        actions={actions}
        processes={processes}
        onDoubleClick={onDoubleClick}
        onQuickChatSubmit={onQuickChatSubmit}
        onDeleteClick={onDeleteClick}
        onDetailClick={onDetailClick}
        toolbarMode={toolbarMode}
        chatOpen={chatOpen}
        onChatOpenChange={onChatOpenChange}
        className={className}
        style={style}
      />
    );
  }

  // ── HTML file records — live sandboxed iframe ─
  if (
    "base" in entity &&
    entity.base === "file_record" &&
    ((entity.data as unknown as FileRecord).content_type ?? "")
      .split(";")[0]
      .trim() === "text/html"
  ) {
    const fr = entity.data as unknown as FileRecord;

    // Block vs document is the caller's resolved display model (record-side
    // truth, node-side paint cache). Splitting here (and only here) is
    // deliberate: the two cards differ in sizing, color scheme and runtime,
    // so the choice must never be made on interactive/edit MODE. Swapping
    // component type on a mode flip would remount the iframe and destroy
    // in-block JS state.
    if (display?.mode === "block") {
      return (
        <HtmlBlockCardSwitch
          kind={fr.display?.kind}
          url={fr.url}
          path={fr.path}
          content_hash={fr.content_hash}
          updated_at={fr.updated_at}
          selected={selected}
          focused={focused}
          resizing={resizing}
          isInteractive={isInteractive}
          display={display}
          theme={theme}
          transformHtml={transformHtml}
          mountPolicy={mountPolicy}
          previewImageUrl={previewImageUrl}
          islandsEditable={islandsEditable}
          contentFrozen={contentFrozen}
          hintLabel={hintLabel}
          islandHint={islandHint}
          editFocusPoint={editFocusPoint}
          onRuntimeReady={onRuntimeReady}
          onBlockMessage={onBlockMessage}
          onNavigate={onBlockNavigate}
          onAction={onBlockAction}
          clickThrough={blockClickThrough}
          onInteractiveClickThrough={onBlockInteractiveClickThrough}
          blockState={blockState}
          className={className}
          style={style}
          onDoubleClick={onDoubleClick}
        />
      );
    }

    // An html DOCUMENT is a document: it takes the shared EntityCard path
    // below (portrait box, filename footer) with the live document in the
    // well, exactly like markdown.
  }

  // ── Documents (markdown; html in document mode) — live, inert well ──────
  const documentRecord =
    "base" in entity && entity.base === "file_record"
      ? (entity.data as unknown as FileRecord)
      : null;
  const documentContentType = (documentRecord?.content_type ?? "")
    .split(";")[0]
    .trim();
  // A web-page capture stores extracted markdown but SHOWS its screenshot
  // (isPageFileRecord): it takes the poster path, never the live well.
  const documentWell =
    documentRecord &&
    isPageFileRecord(documentRecord) &&
    (documentContentType === "text/markdown" ||
      documentContentType === "text/html")
      ? () => (
          <DocumentCardWell
            url={documentRecord.url}
            path={documentRecord.path}
            content_hash={documentRecord.content_hash}
            updated_at={documentRecord.updated_at}
            contentType={documentContentType}
            transformHtml={transformHtml}
            mountPolicy={mountPolicy}
            previewImageUrl={previewImageUrl}
          />
        )
      : undefined;

  // ── File-ish entities: file/file_record ──────────────────────────────────
  // All render as embedded `EntityCard`s on canvas (no card chrome —
  // the React Flow node provides it). The footer is only shown for non-
  // image/video types, where the visual is just a file icon and the
  // filename is the only way to tell tiles apart.
  const isFileish =
    "base" in entity &&
    (entity.base === "file" || entity.base === "file_record");

  // Generic entities carrying a cover image render as the same embedded tile.
  // (image_url is cover-only by contract, so its presence reliably means
  // "show a media tile" — avatar-only generics stay list rows.)
  const isGenericTile =
    "base" in entity &&
    entity.base === "generic" &&
    !!(entity.data as { image_url?: string }).image_url;

  let showFooter: boolean | undefined = undefined;
  if (isFileish) {
    const ct = ((entity.data as { content_type?: string; mime_type?: string })
      .content_type ??
      (entity.data as { mime_type?: string }).mime_type ??
      "") as string;
    const isImageOrVideo = ct.startsWith("image/") || ct.startsWith("video/");
    showFooter = !isImageOrVideo;
  } else if (isGenericTile) {
    // Generic tiles always keep the title footer — the cover alone rarely
    // identifies the record.
    showFooter = true;
  }

  return (
    <EntityCard
      entity={entity}
      actions={actions}
      processes={processes}
      onDoubleClick={onDoubleClick}
      onQuickChatSubmit={onQuickChatSubmit}
      onDeleteClick={onDeleteClick}
      onDetailClick={onDetailClick}
      toolbarMode={toolbarMode}
      chatOpen={chatOpen}
      onChatOpenChange={onChatOpenChange}
      className={className}
      style={style}
      isReference={isReference}
      selected={selected}
      showFooter={showFooter}
      embedded={isFileish || isGenericTile || undefined}
      thumbnailWrapper={documentWell}
      onVideoDisplayReady={onVideoDisplayReady}
      onMediaError={onMediaError}
    />
  );
});
